import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaBolt, FaInfoCircle } from 'react-icons/fa';
import { useStampingPreviews } from '~/resources/loyalty/stamp-template/hooks/useStampingPreviews';
import DynamicFieldGenerator from '~/components/DynamicFieldGenerator';
import type { ActionSchema } from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import type { CheckerValue } from '~/components/StampCard/CheckerSelector';

export interface ActionValue {
  code: string;
  params: Record<string, any>;
}

interface ActionSelectorProps {
  value: ActionValue;
  onChange: (action: ActionValue) => void;
  availableActions: ActionSchema[];
  checkers?: CheckerValue[]; // NEW: Optional checkers for context
  error?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const ActionSelector: React.FC<ActionSelectorProps> = ({
  value,
  onChange,
  availableActions,
  checkers,
  error,
  label = 'Action',
  required = false,
  disabled = false
}) => {
  const [selectedAction, setSelectedAction] = useState<ActionSchema | null>(null);
  const { getActionPreview } = useStampingPreviews({ availableActions });

  // Update selected action when value changes
  useEffect(() => {
    const action = availableActions.find(a => a.code === value?.code);
    setSelectedAction(action || null);
  }, [value?.code, availableActions]);

  // Handle action type change
  const handleActionChange = (actionCode: string) => {
    if (!actionCode) {
      onChange({ code: '', params: {} });
      return;
    }
    
    const action = availableActions.find(a => a.code === actionCode) as ActionSchema;
    if (action) {
      // Initialize params with defaults
      const defaultParams: Record<string, any> = {};
      Object.entries(action.paramSchema).forEach(([key, schema]) => {
        if (schema.default !== undefined) {
          defaultParams[key] = schema.default;
        } else if (schema.required) {
          // Set sensible defaults for required fields
          if (schema.type === 'number') {
            defaultParams[key] = schema.min || 1;
          } else if (schema.type === 'string') {
            defaultParams[key] = '';
          } else if (schema.type === 'select' && schema.options?.length) {
            defaultParams[key] = schema.options[0];
          }
        }
      });
      
      onChange({
        code: actionCode,
        params: defaultParams
      });
    }
  };

  // Handle parameter changes
  const handleParamsChange = (params: Record<string, any>) => {
    onChange({
      ...value,
      params
    });
  };

  return (
    <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-white hover:border-yellow-300 transition-colors">
      {/* Action Type Selection */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <FaBolt className="text-yellow-500" />
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
        
        <div className="relative">
          <select
            value={value?.code || ''}
            onChange={(e) => handleActionChange(e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors appearance-none ${
              error ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          >
            <option value="">Select an action...</option>
            {availableActions.map((action) => (
              <option key={action.code} value={action.code}>
                {action.name}
              </option>
            ))}
          </select>
          <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Action Description and Helper */}
        {selectedAction && (
          <>
            <p className="text-xs text-gray-600 italic pl-1">
              {selectedAction.description}
            </p>
            
            {/* Context-aware helper for manual stamps */}
            {selectedAction.code === 'manual-stamps' && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs">
                    <p className="font-semibold text-blue-900 mb-1">Who receives the stamps?</p>
                    <ul className="space-y-1 text-blue-800">
                      <li>• <strong>Order Amount rules:</strong> Customer who placed the order</li>
                      <li>• <strong>Referral Amount rules:</strong> Person who made the referral</li>
                      <li>• <strong>Check-in rules:</strong> Customer who checked in</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dynamic Parameters */}
      {selectedAction && Object.keys(selectedAction.paramSchema).length > 0 && (
        <div className="pl-4 border-l-2 border-yellow-200">
          <DynamicFieldGenerator
            schema={selectedAction.paramSchema}
            values={value?.params || {}}
            onChange={handleParamsChange}
            errors={error ? { params: error } : {}}
            disabled={disabled}
          />
        </div>
      )}

      {/* Action Preview */}
      {selectedAction && value?.code && (
        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm font-medium text-yellow-800">
            <span className="text-xs uppercase tracking-wide text-yellow-600">Result:</span>
            <br />
            <span className="text-base">🎯 {getActionPreview(value, selectedAction, checkers)}</span>
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <span>⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
};

export default ActionSelector;